/**
 * Insertion (02 §3): the Osprey's north-to-south recon pass, kicking out up to three supply
 * pods before the jump light turns green, the jump, and steering the parafoil down. This
 * module owns `world.insertion`. The mission clock does not start until touchdown.
 */

import { podWind } from '../conditions';
import type { Ctx } from '../ctx';
import { woundRanger } from '../damage';
import { cos, sin } from '../math';
import { inPond, inTrench, resolveMovement } from '../statics';
import { INSERTION, RANGER, STANDARD_ISSUE } from '../tuning';
import { DT, type InsertionState, type Loadout, type MissionDef, type Pod, type PodContents } from '../types';

/**
 * Mission 32 (10 §2): he starts a prisoner, and his pods "fell outside the wire" - they already lie at
 * `def.podsAt`. What he would have carried was taken off him, so it always lands in pod A - on top of whatever
 * was packed in pod A, and whether or not any pods were packed at all (a pod on the ground has no weight cap).
 */
function capturedPods(def: MissionDef, loadout: Loadout | undefined): Pod[] {
  const contents: PodContents[] = (loadout?.pods ?? []).slice(0, 3).map((pod) => ({ ...pod }));
  const carried = loadout?.carried ?? STANDARD_ISSUE;
  const podA = contents[0] ?? { magazines: 0, roundsInMag: 0, grenades: 0, laws: 0, bombs: 0, firstAid: 0 };
  contents[0] = {
    magazines: podA.magazines + carried.magazines, roundsInMag: podA.roundsInMag + carried.roundsInMag,
    grenades: podA.grenades + carried.grenades, laws: podA.laws + carried.laws, bombs: podA.bombs + carried.bombs,
    firstAid: podA.firstAid + carried.firstAid,
  };
  const pods: Pod[] = [];
  (def.podsAt ?? []).slice(0, 3).forEach((at, index) => {
    const inside = contents[index];
    if (inside) pods.push({ id: 900 + index, index, x: at.x, z: at.z, y: 0, state: 'landed', ticks: 0, contents: { ...inside } });
  });
  return pods;
}

export function createInsertionState(def: MissionDef, loadout: Loadout | undefined, skip: boolean): InsertionState {
  const pods: Pod[] = def.startCaptured === true ? capturedPods(def, loadout)
    : skip ? [] : (loadout?.pods ?? []).slice(0, 3).map((contents, index) => ({
      id: 900 + index, index, x: 0, z: 0, y: 0, state: 'aboard', ticks: 0, contents: { ...contents },
    }));
  return {
    phase: skip ? 'ground' : 'flyover',
    x: def.sizeX / 2,
    z: -INSERTION.leadIn,
    prevX: def.sizeX / 2,
    prevZ: -INSERTION.leadIn,
    altitude: INSERTION.altitude,
    heading: 0,
    greenLight: false,
    pods,
    canopy: null,
    scrubbed: false,
  };
}

/** Anything solid, wet, dug or mined under this point? A pod landing on it is lost (02 §3.2). */
function badGround(ctx: Ctx, x: number, z: number): boolean {
  const def = ctx.statics.def;
  if (x < 1 || z < 1 || x > def.sizeX - 1 || z > def.sizeZ - 1) return true;
  if (inTrench(def, x, z) || inPond(def, x, z)) return true;
  for (const f of def.mineFields) if (x >= f.rect.x0 && x <= f.rect.x1 && z >= f.rect.z0 && z <= f.rect.z1) return true;
  const free = resolveMovement(ctx.statics, x, z, 0.5);
  return Math.abs(free.x - x) > 0.05 || Math.abs(free.z - z) > 0.05;
}

function stepPods(ctx: Ctx): void {
  const ins = ctx.world.insertion;
  for (const pod of ins.pods) {
    if (pod.state !== 'falling') continue;
    // Scattered Drop (04 §7): each pod has a wind of its own (conditions.ts); otherwise this is the mission's.
    const wind = podWind(ctx.statics.def, pod.index);
    pod.ticks++;
    pod.y = Math.max(0, pod.y - (INSERTION.altitude / INSERTION.podFallSeconds) * DT);
    pod.x += wind.x * ctx.world.mods.podDrift * DT;
    pod.z += wind.z * ctx.world.mods.podDrift * DT;
    if (pod.y <= 0) {
      const intact = !badGround(ctx, pod.x, pod.z);
      pod.state = intact ? 'landed' : 'destroyed';
      ctx.events.push({ type: 'podLanded', index: pod.index, intact });
    }
  }
}

function land(ctx: Ctx): void {
  const { world: w, statics } = ctx;
  const ins = w.insertion;
  const def = statics.def;
  const r = w.ranger;
  const offMap = ins.x < 0 || ins.x > def.sizeX || ins.z < 0 || ins.z > def.sizeZ;
  if (offMap || ins.z > def.dropBand.z1 + INSERTION.badlandsMargin) {
    // The badlands south of the zone: the mission is over before it began (01 §4).
    ins.scrubbed = true;
    ctx.events.push({ type: 'landed', hazard: 'badlands' });
    return;
  }
  const spot = resolveMovement(statics, ins.x, ins.z, RANGER.radius);
  const rough = Math.abs(spot.x - ins.x) > 0.05 || Math.abs(spot.z - ins.z) > 0.05 || inTrench(def, ins.x, ins.z);
  const wet = inPond(def, ins.x, ins.z);
  let mined = false;
  for (const m of w.hazards.mines) {
    if (m.live && (m.x - spot.x) * (m.x - spot.x) + (m.z - spot.z) * (m.z - spot.z) < 1.2 * 1.2) {
      m.live = false;
      mined = true;
    }
  }
  r.x = spot.x;
  r.z = spot.z;
  r.prevX = spot.x;
  r.prevZ = spot.z;
  r.facing = ins.heading;
  ins.phase = 'ground';
  ins.canopy = { x: spot.x, z: spot.z + 0.8, hidden: false };
  if (mined) {
    ctx.events.push({ type: 'mine', x: spot.x, z: spot.z });
    woundRanger(ctx, 2, 'landing');
  } else if (rough && ctx.rng.chance(INSERTION.roughLandingWoundChance)) woundRanger(ctx, 1, 'landing');
  ctx.events.push({ type: 'landed', hazard: mined ? 'mine' : wet ? 'water' : rough ? 'rough' : 'none' });
}

/** Runs instead of the ranger's own step until he is on the ground. */
export function stepInsertion(ctx: Ctx): void {
  const { world: w, cmd, statics } = ctx;
  const ins = w.insertion;
  const def = statics.def;
  const r = w.ranger;
  const firePressed = cmd.fire && !r.prevFire;
  r.prevFire = cmd.fire;
  ins.prevX = ins.x;
  ins.prevZ = ins.z;
  stepPods(ctx);

  if (ins.phase === 'flyover') {
    ins.z += INSERTION.flySpeed * DT;
    ins.x = Math.min(def.sizeX - INSERTION.edgeMargin, Math.max(INSERTION.edgeMargin, ins.x + cmd.moveX * INSERTION.steerSpeed * DT));
    ins.greenLight = ins.z >= def.dropBand.z0 && ins.z <= def.dropBand.z1;
    if (firePressed && ins.greenLight) {
      ins.phase = 'descent';
      ins.heading = 0;
      ctx.events.push({ type: 'jump' });
    } else if (firePressed && ins.z < def.dropBand.z0 && ins.z > 0) {
      const pod = ins.pods.find((p) => p.state === 'aboard');
      if (pod) {
        pod.state = 'falling';
        pod.x = ins.x;
        pod.z = ins.z;
        pod.y = INSERTION.altitude;
        ctx.events.push({ type: 'podDropped', index: pod.index });
      }
    }
    // Never jumped: the Osprey turns for home with the ranger still aboard.
    if (ins.z > def.dropBand.z1 + INSERTION.badlandsMargin) ins.scrubbed = true;
  } else if (ins.phase === 'descent') {
    // A parafoil glides forward; turning spills air, so a turn costs height (01 §4).
    const turn = cmd.moveX * INSERTION.turnRate * w.mods.parafoilSteering * DT;
    ins.heading -= turn;
    const sink = INSERTION.sinkRate * (1 + INSERTION.turnSinkPenalty * Math.min(1, Math.abs(cmd.moveX)));
    ins.altitude -= sink * DT;
    ins.x += (sin(ins.heading) * INSERTION.glideSpeed + def.wind.x) * DT;
    ins.z += (cos(ins.heading) * INSERTION.glideSpeed + def.wind.z) * DT;
    if (ins.altitude <= 0) {
      ins.altitude = 0;
      land(ctx);
    }
  }

  // Until touchdown the ranger "is" wherever the aircraft or canopy is, out of everyone's sight.
  if (ins.phase !== 'ground') {
    r.x = ins.x;
    r.z = ins.z;
    r.prevX = ins.prevX;
    r.prevZ = ins.prevZ;
  }
}

/** A landed pod within reach, for the Interact prompt. */
export function podInReach(ctx: Pick<Ctx, 'world'>): Pod | null {
  const r = ctx.world.ranger;
  for (const pod of ctx.world.insertion.pods) {
    if (pod.state !== 'landed') continue;
    if ((pod.x - r.x) * (pod.x - r.x) + (pod.z - r.z) * (pod.z - r.z) <= INSERTION.podReach * INSERTION.podReach) return pod;
  }
  return null;
}
